import React, { useState, useRef } from "react";
import { Dropdown, Offcanvas } from "react-bootstrap";
import { FaDownload, FaFileCsv, FaFilePdf, FaFileExcel, FaPlus, FaFileInvoice, FaEdit, FaSave, FaEuroSign, FaPercent } from "react-icons/fa";
import { BsCalculator } from "react-icons/bs";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { 
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Divider,
  CircularProgress,
  TextField,
  LinearProgress
} from '@mui/material';
import { BillingUtils, PatientBillingService } from '../Services/billingService';
import SplitConfiguration from '../Popup/SplitConfigurationGeneral';
import { API_BASE_URL } from "helpers/urlHelper";
import { 
  getPricelistsCustomer, 
  getPricelistsSupplier,
  getProducts 
} from '../../ProductCatalog/Service/productServices';



const BillingActionButtonGeneral = ({ 
  selectedActivities = [], 
  onCreateBillableActivities,
  refreshData,
  currentUserRoles = [],
  isProcessingBulkAction = false
}) => {
  const [isCreating, setIsCreating] = useState(false);
  
  // Estados para el offcanvas de facturación
  const [showInvoiceOffcanvas, setShowInvoiceOffcanvas] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [invoiceData, setInvoiceData] = useState({
    grouping_mode: 'PAYER_PATIENT',
    additional_notes: ''
  });
  const [createdInvoices, setCreatedInvoices] = useState(null);
  const [invoiceError, setInvoiceError] = useState(null);
  const [patientsData, setPatientsData] = useState([]);
  const [clinicsData, setClinicsData] = useState([]);
  
  // Estados para configuración de precios y splits
  const [activityPricing, setActivityPricing] = useState(new Map());
  const [activitySplits, setActivitySplits] = useState(new Map());
  const [editingPricing, setEditingPricing] = useState(null);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const offcanvasRef = useRef(null);
  const [loadedPrices, setLoadedPrices] = useState(new Map());
  // Estados para selectores de precios
const [customerPricelists, setCustomerPricelists] = useState(new Map());
const [supplierPricelists, setSupplierPricelists] = useState(new Map());
const [loadingPricelists, setLoadingPricelists] = useState(new Map());
const [selectedCustomerPricelist, setSelectedCustomerPricelist] = useState(new Map());
const [selectedSupplierPricelist, setSelectedSupplierPricelist] = useState(new Map());
const [displayedCustomerPrices, setDisplayedCustomerPrices] = useState(new Map());
const [displayedSupplierPrices, setDisplayedSupplierPrices] = useState(new Map());

const [isExportingToOdoo, setIsExportingToOdoo] = useState(false);
const [exportResults, setExportResults] = useState(null);

// Después de los estados de pricelists existentes, agrega:
const [productPricelists, setProductPricelists] = useState(new Map());
const [selectedProductPricelist, setSelectedProductPricelist] = useState(new Map());
const [displayedProductPrices, setDisplayedProductPrices] = useState(new Map());

// ============================================
// 🔧 ESTADOS PARA LOS TRES TIPOS DE PAGADORES
// ============================================
const [availablePatients, setAvailablePatients] = useState([]);
const [availableInsurers, setAvailableInsurers] = useState([]);
const [availableHealthCenters, setAvailableHealthCenters] = useState([]);
const [loadingPayers, setLoadingPayers] = useState(false);

const wizardSteps = ['Configurar Precios y Splits', 'Modo de Agrupación','Resultado'];


// ============================================
// 🔧 FUNCIÓN PARA COMPLETAR partner_payload
// ============================================

const completePartnerPayload = (partnerPayload, patientsData, clinicsData) => {
  const payerType = partnerPayload.x_medflow_payer_type;
  const payerId = partnerPayload.x_medflow_payer_id;
  
  if (payerType === 'User') {
    // Buscar el paciente por _id
    const patient = patientsData.find(p => p._id === payerId);
    
    if (patient) {
      return {
        ...partnerPayload,
        name: `${patient.name} ${patient.surname}`,
        vat: patient._id || patient.id || payerId, // Los pacientes no tienen CIF/VAT/ ASI QUE SE USA EL _ID
        email: patient.email || null,
        phone: patient.phone || null,
        street: patient.address || null,
        city: patient.city || null,
        zip: patient.zipcode || null,
        country_id: 'ES' // País por defecto España
      };
    }
  } 
  else if (payerType === 'Company') {
    // Buscar la clínica por _id
    const clinic = clinicsData.find(c => c._id === payerId);
    
    if (clinic) {
      return {
        ...partnerPayload,
        name: clinic.name,
        vat: clinic.cif || null,
        email: clinic.email || null,
        phone: clinic.phone || null,
        street: clinic.address || null,
        city: clinic.city || null,
        zip: clinic.zipcode || null,
        country_id: 'ES' // País por defecto España
      };
    }
  }
  
  // Si no se encontró el pagador, devolver el payload original
  return partnerPayload;
};

// ============================================
// 🔧 FUNCIÓN PARA COMPLETAR payer_snapshot
// ============================================

const completePayerSnapshot = (payerSnapshot, patientsData, clinicsData, payerId, payerType) => {
  if (payerType === 'User') {
    // Buscar el paciente por _id
    const patient = patientsData.find(p => p._id === payerId);
    
    if (patient) {
      return {
        name: `${patient.name} ${patient.surname}`,
        display_name: `${patient.name} ${patient.surname}`,
        type_description: 'Paciente',
        address: {
          street: patient.address || '',
          city: patient.city || '',
          state: patient.state || '',
          zipcode: patient.zipcode || ''
        },
        contact: {
          phone: patient.phone || '',
          email: patient.email || ''
        },
        center_info: {
          insurance_contracts: []
        }
      };
    }
  } 
  else if (payerType === 'Company') {
    // Buscar la clínica por _id
    const clinic = clinicsData.find(c => c._id === payerId);
    
    if (clinic) {
      return {
        name: clinic.name,
        display_name: clinic.name,
        type_description: 'Compañía',
        cif: clinic.cif || '',
        address: {
          street: clinic.address || '',
          city: clinic.city || '',
          state: clinic.state || '',
          zipcode: clinic.zipcode || ''
        },
        contact: {
          phone: clinic.phone || '',
          email: clinic.email || '',
          website: clinic.website || ''
        },
        center_info: {
          insurance_contracts: [],
          services: clinic.services || [],
          specialties: clinic.specialties || [],
          departments: clinic.departments || []
        }
      };
    }
  }
  
  // Si no se encontró el pagador, devolver el snapshot original
  return payerSnapshot;
};

// ============================================
// 🔧 FUNCIÓN PRINCIPAL PARA COMPLETAR TODO EL JSON
// ============================================

const completeInvoiceJSON = (invoiceJSON, patientsData, clinicsData) => {
  // 1. Completar partner_payload
  const completedPartnerPayload = completePartnerPayload(
    invoiceJSON.partner_payload,
    patientsData,
    clinicsData
  );
  
  // 2. Completar payer_snapshot
  const completedPayerSnapshot = completePayerSnapshot(
    invoiceJSON.payer_snapshot,
    patientsData,
    clinicsData,
    invoiceJSON.x_medflow_payer_id,
    invoiceJSON.x_medflow_payer_type
  );
  
  // 3. Corregir x_medflow_patient_id si es "[object Object]"
  let completedPatientId = invoiceJSON.x_medflow_patient_id;
  if (typeof completedPatientId === 'string' && completedPatientId === '[object Object]') {
    // Intentar obtener el patient_id desde las líneas de factura
    if (invoiceJSON.invoice_line_ids && invoiceJSON.invoice_line_ids.length > 0) {
      completedPatientId = invoiceJSON.invoice_line_ids[0].x_medflow_patient_id;
    }
  }
  
  return {
    ...invoiceJSON,
    partner_payload: completedPartnerPayload,
    payer_snapshot: completedPayerSnapshot,
    x_medflow_patient_id: completedPatientId
  };
};


const fetchPricelistsForActivity = async (activity, activityKey, type = 'both') => {
  try {
    const loadingMap = new Map(loadingPricelists);
    loadingMap.set(`${activityKey}_${type}`, true);
    setLoadingPricelists(loadingMap);

    console.log('🔍 Cargando pricelists para:', { 
      activity: activity.description, 
      type,
      patient_id: activity.patient_id 
    });

    // Cargar Customer Pricelists
    if (type === 'customer' || type === 'both') {
      const customerResponse = await getPricelistsCustomer({ 
        active: true 
      });
      
      let customerData = [];
      if (Array.isArray(customerResponse)) {
        customerData = customerResponse;
      } else if (customerResponse.data && Array.isArray(customerResponse.data)) {
        customerData = customerResponse.data;
      } else if (customerResponse.data && Array.isArray(customerResponse.data.data)) {
        customerData = customerResponse.data.data;
      }

      const customerMap = new Map(customerPricelists);
      customerMap.set(activityKey, customerData);
      setCustomerPricelists(customerMap);
      console.log('✅ Customer pricelists cargadas:', customerData.length);
    }

    // Cargar Supplier Pricelists
    if (type === 'supplier' || type === 'both') {
      const supplierResponse = await getPricelistsSupplier({ 
        active: true 
      });
      
      let supplierData = [];
      if (Array.isArray(supplierResponse)) {
        supplierData = supplierResponse;
      } else if (supplierResponse.data && Array.isArray(supplierResponse.data)) {
        supplierData = supplierResponse.data;
      } else if (supplierResponse.data && Array.isArray(supplierResponse.data.data)) {
        supplierData = supplierResponse.data.data;
      }

      const supplierMap = new Map(supplierPricelists);
      supplierMap.set(activityKey, supplierData);
      setSupplierPricelists(supplierMap);
      console.log('✅ Supplier pricelists cargadas:', supplierData.length);
    }

  } catch (error) {
    console.error('❌ Error cargando pricelists:', error);
    alert(`Error al cargar tarifas: ${error.message}`);
  } finally {
    const loadingMap = new Map(loadingPricelists);
    loadingMap.set(`${activityKey}_${type}`, false);
    setLoadingPricelists(loadingMap);
  }
};

const fetchProductsForActivity = async (activity, activityKey) => {
  try {
    const loadingMap = new Map(loadingPricelists);
    loadingMap.set(`${activityKey}_product`, true);
    setLoadingPricelists(loadingMap);

    console.log('🔍 Cargando productos del catálogo...');

    const response = await getProducts({ isActive: true });
    
    let productData = [];
    if (Array.isArray(response)) {
      productData = response;
    } else if (response.data && Array.isArray(response.data)) {
      productData = response.data;
    } else if (response.data && Array.isArray(response.data.data)) {
      productData = response.data.data;
    }

    const productMap = new Map(productPricelists);
    productMap.set(activityKey, productData);
    setProductPricelists(productMap);
    console.log('✅ Productos cargados:', productData.length);

  } catch (error) {
    console.error('❌ Error cargando productos:', error);
    alert(`Error al cargar productos: ${error.message}`);
  } finally {
    const loadingMap = new Map(loadingPricelists);
    loadingMap.set(`${activityKey}_product`, false);
    setLoadingPricelists(loadingMap);
  }
};

  // ============================================
  // Funciones de Exportación (mantener las existentes)
  // ============================================
  
  const exportToPDF = (activities) => {
    if (activities.length === 0) return;
  
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    doc.setFontSize(16);
    doc.text("General Activities Report", doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
  
    const allKeys = new Set();
    activities.forEach(activity => {
      Object.keys(activity).forEach(key => {
        if (!key.startsWith('_') && key !== '__v') {
          allKeys.add(key);
        }
      });
    });
  
    const preferredOrder = [
      'id', 'patient_name', 'doctor_name', 'center_name',
      'performed_at', 'activity_type', 'description', 'status', 
      'duration', 'notes', 'is_billable', 'created_at', 'updated_at'
    ];
    
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  
    const formattedActivities = activities.map(activity => {
      const formatted = {};
      
      sortedKeys.forEach(key => {
        if (!activity.hasOwnProperty(key)) {
          formatted[key] = '';
          return;
        }
        
        let value = activity[key];
        
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              value = date.toLocaleDateString();
            }
          } catch (e) {}
        }
        
        if (typeof value === 'boolean') {
          value = value ? 'Yes' : 'No';
        }
        
        if (value !== null && typeof value === 'object') {
          try {
            value = JSON.stringify(value)
              .replace(/[{}"[\]]/g, '')
              .replace(/,/g, ', ')
              .trim();
          } catch (e) {
            value = String(value);
          }
        }
        
        value = value !== null && value !== undefined ? String(value) : '';
        formatted[key] = value;
      });
      
      return formatted;
    });
  
    const formattedHeaders = sortedKeys.map(key => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    });
  
    const tableColumns = formattedHeaders;
    const tableRows = formattedActivities.map(activity => 
      sortedKeys.map(key => activity[key] || '')
    );
  
    const columnWidths = {};
    sortedKeys.forEach((key, index) => {
      let columnWidth;
      
      if (key === 'id' || key === '_id' || key.endsWith('Id')) {
        columnWidth = 25;
      } else if (key.includes('name') || key.includes('Name')) {
        columnWidth = 30;
      } else if (key === 'description' || key === 'notes') {
        columnWidth = 45;
      } else if (key === 'duration') {
        columnWidth = 20;
      } else {
        columnWidth = 25;
      }
      
      columnWidths[index] = { cellWidth: columnWidth };
    });
  
    const pageWidth = doc.internal.pageSize.getWidth() - 20;
    const totalWidthUnits = Object.values(columnWidths).reduce((sum, col) => sum + col.cellWidth, 0);
    const scaleRatio = pageWidth / totalWidthUnits;
    
    Object.keys(columnWidths).forEach(index => {
      columnWidths[index].cellWidth = columnWidths[index].cellWidth * scaleRatio;
    });
  
    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 20,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        halign: 'left',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: columnWidths,
      margin: { top: 20, right: 10, bottom: 15, left: 10 },
      tableWidth: 'auto',
      showHead: 'everyPage',
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 5,
          { align: 'center' }
        );
        
        const dateText = `Generated: ${new Date().toLocaleString()} | Total Records: ${tableRows.length}`;
        doc.text(dateText, 10, pageHeight - 5);
        
        if (data.pageNumber > 1) {
          doc.setFontSize(14);
          doc.setTextColor(0);
          doc.text("General Activities Report (Continued)", pageWidth / 2, 10, { align: 'center' });
        }
      }
    });
  
    const date = new Date().toISOString().slice(0, 10);
    doc.save(`general_activities_${date}.pdf`);
  };
  
  const exportToCSV = (activities) => {
    if (activities.length === 0) return;
  
    const preferredOrder = [
      'id', 'patient_name', 'doctor_name', 'center_name',
      'performed_at', 'activity_type', 'description', 'status', 
      'duration', 'notes', 'is_billable', 'created_at', 'updated_at'
    ];
  
    const allKeys = new Set();
    activities.forEach(activity => {
      Object.keys(activity).forEach(key => {
        if (!key.startsWith('_') && key !== '__v') {
          allKeys.add(key);
        }
      });
    });
  
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  
    const processedActivities = activities.map(activity => {
      const processed = {};
      
      sortedKeys.forEach(key => {
        if (!activity.hasOwnProperty(key)) {
          processed[key] = '';
          return;
        }
        
        let value = activity[key];
        
        if (value && typeof value === 'string' && 
            (value.match(/^\d{4}-\d{2}-\d{2}T/) || value.match(/^\d{4}-\d{2}-\d{2}/))) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              processed[key] = date.toLocaleDateString();
            } else {
              processed[key] = value;
            }
          } catch (e) {
            processed[key] = value;
          }
        } 
        else if (typeof value === 'object' && value !== null) {
          try {
            processed[key] = JSON.stringify(value)
              .replace(/[{}"[\]]/g, '')
              .replace(/,/g, ', ')
              .trim();
          } catch (e) {
            processed[key] = String(value);
          }
        }
        else if (typeof value === 'boolean') {
          processed[key] = value ? 'Yes' : 'No';
        }
        else if (value === undefined || value === null) {
          processed[key] = '';
        }
        else {
          processed[key] = value;
        }
      });
      
      return processed;
    });
  
    const headers = sortedKeys.map(key => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    });
    
    const headerRow = headers
      .map(header => `"${header}"`)
      .join(',');
    
    const dataRows = processedActivities.map(activity => 
      sortedKeys.map(key => {
        const value = activity[key];
        if (value === null || value === undefined || value === '') return '""';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    ).join('\n');
    
    const csvContent = `data:text/csv;charset=utf-8,${headerRow}\n${dataRows}`;
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    
    const date = new Date().toISOString().slice(0, 10);
    link.setAttribute("download", `general_activities_${date}.csv`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const exportToExcel = (activities) => {
    if (activities.length === 0) return;
  
    const processedActivities = activities.map(activity => {
      const processed = {};
      
      Object.keys(activity).forEach(key => {
        if (activity[key] && typeof activity[key] === 'string' && 
            activity[key].match(/^\d{4}-\d{2}-\d{2}T/)) {
          processed[key] = activity[key];
        } 
        else if (typeof activity[key] === 'object' && activity[key] !== null) {
          processed[key] = JSON.stringify(activity[key]);
        }
        else {
          processed[key] = activity[key];
        }
      });
      
      return processed;
    });
  
    const worksheet = XLSX.utils.json_to_sheet(processedActivities);
    
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    const columnWidths = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxColumnWidth = 10;
      
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({r: R, c: C});
        const cell = worksheet[cellAddress];
        
        if (!cell) continue;
        
        const cellContent = String(cell.v || '');
        const estimatedWidth = Math.min(50, cellContent.length * 1.2);
        maxColumnWidth = Math.max(maxColumnWidth, estimatedWidth);
      }
      
      columnWidths[C] = { width: maxColumnWidth };
    }
    
    worksheet['!cols'] = columnWidths;
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "General Activities");
    
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const headerAddress = XLSX.utils.encode_cell({r: 0, c: C});
      if (!worksheet[headerAddress]) continue;
      
      const headerText = worksheet[headerAddress].v;
      worksheet[headerAddress].v = typeof headerText === 'string' ? 
        headerText.charAt(0).toUpperCase() + headerText.slice(1) : 
        headerText;
    }
    
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `general_activities_${date}.xlsx`);
  };
  
  const printActivities = (activities) => {
    if (activities.length === 0) return;
  
    const preferredOrder = [
      'id', 'patient_name', 'doctor_name', 'center_name',
      'performed_at', 'activity_type', 'description', 'status', 
      'duration', 'notes', 'is_billable'
    ];
  
    const allKeys = new Set();
    activities.forEach(activity => {
      Object.keys(activity).forEach(key => {
        if (!key.startsWith('_') && key !== '__v') {
          allKeys.add(key);
        }
      });
    });
  
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  
    const processedData = activities.map(activity => {
      const processed = {};
      
      sortedKeys.forEach(key => {
        if (!activity.hasOwnProperty(key)) {
          processed[key] = '';
          return;
        }
        
        let value = activity[key];
        
        if (value && typeof value === 'string' && 
            (value.match(/^\d{4}-\d{2}-\d{2}T/) || value.match(/^\d{4}-\d{2}-\d{2}/))) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              processed[key] = date.toLocaleDateString();
            } else {
              processed[key] = value;
            }
          } catch (e) {
            processed[key] = value;
          }
        } 
        else if (typeof value === 'object' && value !== null) {
          try {
            processed[key] = JSON.stringify(value)
              .replace(/[{}"[\]]/g, '')
              .replace(/,/g, ', ')
              .trim();
          } catch (e) {
            processed[key] = String(value);
          }
        }
        else if (typeof value === 'boolean') {
          processed[key] = value ? 'Yes' : 'No';
        }
        else if (value === undefined || value === null) {
          processed[key] = '';
        }
        else {
          processed[key] = value;
        }
      });
      
      return processed;
    });
      
    const headers = sortedKeys.map(key => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    });
    
    const printWindow = window.open('', 'PRINT', 'height=800,width=1200');
    
    const tableHTML = `
      <html>
        <head>
          <title>General Activities Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0;
              padding: 20px;
            }
            h1 { 
              text-align: center; 
              margin-bottom: 20px;
            }
            .print-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              font-size: 12px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px;
              font-size: 11px;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 6px; 
              text-align: left;
              vertical-align: top;
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold;
              position: sticky;
              top: 0;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            @page { 
              size: landscape; 
              margin: 0.5in;
            }
            thead { display: table-header-group; }
            tr { page-break-inside: avoid; }
            td.long-text {
              max-width: 200px;
              word-wrap: break-word;
            }
            ${sortedKeys.map((key, index) => {
              let width;
              if (key === 'id' || key === '_id' || key.endsWith('Id')) {
                width = '80px';
              } else if (key.includes('name') || key.includes('Name')) {
                width = '120px';
              } else if (key === 'description' || key === 'notes') {
                width = '200px; overflow-wrap: break-word;';
              } else if (key === 'duration') {
                width = '80px';
              } else if (key.includes('date') || key.includes('Date')) {
                width = '100px';
              } else {
                width = '100px';
              }
              return `.col-${index} { width: ${width}; }`;
            }).join('\n')}
          </style>
        </head>
        <body>
          <h1>General Activities Report</h1>
          <div class="print-info">
            <div>Total Items: ${processedData.length}</div>
            <div>Generated on: ${new Date().toLocaleString()}</div>
          </div>
          <table>
            <thead>
              <tr>
                ${headers.map((header, index) => `<th class="col-${index}">${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${processedData.map(row => 
                `<tr>
                  ${sortedKeys.map((key, index) => {
                    const value = row[key] !== null && row[key] !== undefined ? row[key] : '';
                    const className = (key === 'description' || key === 'notes') ? 'long-text' : '';
                    return `<td class="col-${index} ${className}">${value}</td>`;
                  }).join('')}
                </tr>`
              ).join('')}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `;
  
    printWindow.document.write(tableHTML);
    printWindow.document.close();
    printWindow.focus();
  };

  const handleCreateBillableActivities = async () => {
    if (selectedActivities.length === 0) {
      alert('Please select activities to convert to billable activities');
      return;
    }
    
    const nonBillableActivities = selectedActivities.filter(activity => !activity.is_billable);
    
    if (nonBillableActivities.length === 0) {
      alert('All selected activities are already marked as billable');
      return;
    }
    
    if (window.confirm(`Create billable activities from ${nonBillableActivities.length} selected activities?`)) {
      setIsCreating(true);
      try {
        await onCreateBillableActivities(nonBillableActivities);
        alert(`Successfully created billable activities for ${nonBillableActivities.length} activities`);
        
        if (refreshData) {
          refreshData();
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error('Error creating billable activities:', error);
        alert(`An unexpected error occurred: ${error.message}`);
      } finally {
        setIsCreating(false);
      }
    }
  };

  // ============================================
  // 🎯 FUNCIONALIDAD: Crear Facturas con Offcanvas
  // ============================================

const handleOpenInvoiceWizard = async () => {
  if (selectedActivities.length === 0) {
    alert('Por favor seleccione actividades para facturar');
    return;
  }
  
  setIsCreating(true);
  try {
    console.log('🔄 Cargando datos de pacientes, aseguradoras y centros médicos...');
    
    const [patients, insurers, healthCenters] = await Promise.all([
      fetchAvailablePatients(),
      fetchAvailableInsurers(),
      fetchAvailableHealthCenters()
    ]);
    
    setPatientsData(patients);
    setClinicsData([...insurers, ...healthCenters]);
    
    console.log('✅ Datos cargados correctamente:', {
      patients: patients.length,
      insurers: insurers.length,
      healthCenters: healthCenters.length
    });
  } catch (error) {
    console.error('❌ Error cargando datos:', error);
    alert('Error al cargar datos de pacientes y clínicas');
    setIsCreating(false);
    return;
  }
  setIsCreating(false);
  
  // Inicializar precios por defecto
  const defaultPricingMap = new Map();
  selectedActivities.forEach(activity => {
    const key = `${activity.element_id}_${activity.activity_type}`;
    defaultPricingMap.set(key, {
      unit_price: activity.unit_price || 75,
      qty: activity.quantity || 1,
      discount: 0,
      tax_profile: 'EXENTO',
      tax_rate: 0,
      notes: ''
    });
  });
  setActivityPricing(defaultPricingMap);

  // 🆕 INICIALIZAR SPLITS CON DATOS COMPLETOS DEL PACIENTE
  const defaultSplitsMap = new Map();
  
  // ✅ USAR for...of EN LUGAR DE forEach
  for (const activity of selectedActivities) {
    const key = `${activity.element_id}_${activity.activity_type}`;
    
    // Buscar el paciente en los datos ya cargados (patientsData)
    const patient = (await fetchAvailablePatients()).find(p => p._id === activity.patient_id);
    
    if (!patient) {
      console.warn(`⚠️ Paciente ${activity.patient_id} no encontrado en la lista`);
    }
    
    defaultSplitsMap.set(key, {
      splits: [{
        payer_id: patient?._id || activity.patient_id,
        payer_type: 'User',
        percent: 100,
        max_amount: null,
        payer_snapshot: {
          name: patient ? `${patient.name} ${patient.surname}` : activity.patient_name,
          display_name: patient ? `${patient.name} ${patient.surname}` : activity.patient_name,
          type_description: 'Paciente',
          contact: {
            phone: patient?.phone || '',
            email: patient?.email || ''
          },
          address: {
            street: patient?.address || '',
            city: patient?.city || '',
            state: patient?.state || '',
            zipcode: patient?.zipcode || ''
          }
        }
      }],
      isValid: true
    });
  }
  
  setActivitySplits(defaultSplitsMap);
  
  setActiveStep(0);
  setInvoiceError(null);
  setCreatedInvoices(null);
  setShowInvoiceOffcanvas(true);
};

  const handleCloseInvoiceOffcanvas = () => {
    setShowInvoiceOffcanvas(false);
    setActiveStep(0);
    setInvoiceData({
      grouping_mode: 'PAYER_PATIENT',
      additional_notes: ''
    });
    setInvoiceError(null);
    setActivityPricing(new Map());
    setActivitySplits(new Map());
    setEditingPricing(null);
  };


// ============================================
// 📥 FETCH 1: CARGAR PACIENTES
// ============================================
const fetchAvailablePatients = async (currentPatientId) => {
  try {
    setLoadingPayers(true);
    console.log('🔍 Cargando pacientes disponibles...');
    
    const response = await fetch(`${API_BASE_URL}/users?role=patient`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Error al cargar pacientes');
    }

    const data = await response.json();
    const allPatients = data.data || data || [];
    
    // Filtrar el paciente actual SI se proporciona
    const otherPatients = currentPatientId 
      ? allPatients.filter(p => p._id !== currentPatientId)
      : allPatients;
    
    const formattedPatients = otherPatients.map(patient => ({
      // IDs
      _id: patient._id,
      customid: patient.customid,
      
      // Datos básicos
      name: patient.name,
      surname: patient.surname,
      fullName: `${patient.name} ${patient.surname}`,
      email: patient.email || null,
      phone: patient.phone || null,
      
      // Dirección
      address: patient.address || null,
      city: patient.city || null,
      state: patient.state || null,
      zipcode: patient.zipcode || null,
      
      // Datos médicos
      dob: patient.dob || null,
      gender: patient.gender || null,
      bloodgroup: patient.bloodgroup || null,
      height: patient.height || null,
      weight: patient.weight || null,
      allergies: patient.allergies || [],
      habits: patient.habits || [],
      
      // Seguro médico
      insurance: patient.insurance || false,
      insuranceNumber: patient.insuranceNumber || null,
      insuranceCompany: patient.insuranceCompany || null,
      insuranceExpirationDate: patient.insuranceExpirationDate || null,
      
      // Contacto de emergencia
      emergencycontact: patient.emergencycontact || null,
      emergencycontactname: patient.emergencycontactname || null,
      emergencycontactrelation: patient.emergencycontactrelation || null,
      
      // Otros
      about: patient.about || null,
      services: patient.services || [],
      languages: patient.languages || [],
      company: patient.company || null,
      isActive: patient.isActive !== false,
      assignedDoctor: patient.assignedDoctor || null,
      
      // Tipo para el JSON
      type: 'User',
      display_name: `${patient.name} ${patient.surname}`,
      type_description: 'Paciente'
    }));
    
    console.log('✅ Pacientes disponibles cargados:', formattedPatients.length);
    setAvailablePatients(formattedPatients);
    
    return formattedPatients; // ⬅️ CRÍTICO: RETORNAR LOS DATOS
    
  } catch (error) {
    console.error('❌ Error cargando pacientes:', error);
    setAvailablePatients([]);
    return []; // ⬅️ RETORNAR ARRAY VACÍO EN CASO DE ERROR
  } finally {
    setLoadingPayers(false);
  }
};

// ============================================
// 📥 FETCH 2: CARGAR ASEGURADORAS
// ============================================
const fetchAvailableInsurers = async () => {
  try {
    setLoadingPayers(true);
    console.log('🔍 Cargando aseguradoras disponibles...');
    
    const response = await fetch(`${API_BASE_URL}/companies?companyType=Aseguradora`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Error al cargar aseguradoras');
    }

    const result = await response.json();
    
    // Manejar diferentes estructuras de respuesta
    let companies = [];
    if (Array.isArray(result)) {
      companies = result;
    } else if (result.data && Array.isArray(result.data)) {
      companies = result.data;
    } else if (result.companies && Array.isArray(result.companies)) {
      companies = result.companies;
    }
    
    // Formatear aseguradoras con todos los datos necesarios
    const insurers = companies.map(company => ({
      // IDs
      _id: company._id,
      id: company.id,
      
      // Datos básicos
      name: company.name,
      cif: company.cif || null,
      email: company.email || null,
      phone: company.phone || null,
      website: company.website || null,
      
      // Dirección (desde companymedicalextension o directamente)
      address: company.address || null,
      city: company.companymedicalextension?.city || null,
      state: company.companymedicalextension?.state || null,
      zipcode: company.companymedicalextension?.zipcode || null,
      
      // Datos de la extensión médica
      medicalExtension: {
        type: company.companymedicalextension?.type || 'Aseguradora de Salud',
        departments: company.companymedicalextension?.departments || [],
        facilities: company.companymedicalextension?.facilities || [],
        equipments: company.companymedicalextension?.equipments || [],
        languages: company.companymedicalextension?.languages || [],
        paymentMethod: company.companymedicalextension?.paymentMethod || [],
        insurance: company.companymedicalextension?.insurance || [],
        hasParking: company.companymedicalextension?.hasParking || false,
        wheelChairAccess: company.companymedicalextension?.wheelChairAccess || false,
        specialties: company.companymedicalextension?.specialties || [],
        hours: company.companymedicalextension?.hours || [],
        medicaid: company.companymedicalextension?.medicaid || false,
        accessibility: company.companymedicalextension?.accessibility || [],
        rating: company.companymedicalextension?.rating || null
      },
      
      // Otros
      companyType: company.companyType || 'Aseguradora',
      about: company.about || null,
      logo: company.logo || null,
      uploads: company.uploads || [],
      services: company.services || [],
      departments: company.departments || [],
      isactive: company.isactive !== false,
      status: company.status || '1',
      estd: company.estd || null,
      
      // Tipo para el JSON
      type: 'Company',
      display_name: company.name,
      type_description: 'Aseguradora'
    }));
    
    console.log('✅ Aseguradoras disponibles cargadas:', insurers.length);
    setAvailableInsurers(insurers);
    return insurers;
    
  } catch (error) {
    console.error('❌ Error cargando aseguradoras:', error);
    setAvailableInsurers([]);
    return [];
  } finally {
    setLoadingPayers(false);
  }
};

// ============================================
// 📥 FETCH 3: CARGAR CENTROS MÉDICOS
// ============================================
const fetchAvailableHealthCenters = async () => {
  try {
    setLoadingPayers(true);
    console.log('🔍 Cargando centros médicos disponibles...');
    
    const response = await fetch(`${API_BASE_URL}/healthCenters/clinics`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Error al cargar centros médicos');
    }

    const result = await response.json();
    const clinics = result.data || result || [];
    
    // Formatear centros médicos con todos los datos necesarios
    const healthCenters = clinics.map(clinic => ({
      // IDs
      _id: clinic._id,
      
      // Datos básicos
      name: clinic.name,
      type: clinic.type || 'Centro médico',
      cif: clinic.cif || null,
      email: clinic.email || null,
      phone: clinic.phone || null,
      website: clinic.website || null,
      
      // Dirección
      address: clinic.address || null,
      city: clinic.city || null,
      state: clinic.state || null,
      zipcode: clinic.zipcode || null,
      
      // Servicios y departamentos
      services: clinic.services || [],
      departments: clinic.departments || [],
      facilities: clinic.facilities || [],
      equipments: clinic.equipments || [],
      specialties: clinic.specialties || [],
      
      // Accesibilidad
      languages: clinic.languages || [],
      paymentMethod: clinic.paymentMethod || [],
      insurance: clinic.insurance || [],
      hasParking: clinic.hasParking || false,
      wheelChairAccess: clinic.wheelChairAccess || false,
      accessibility: clinic.accessibility || [],
      
      // Horarios y otros
      hours: clinic.hours || [],
      medicaid: clinic.medicaid || false,
      rating: clinic.rating || null,
      about: clinic.about || null,
      uploads: clinic.uploads || [],
      
      // Fechas
      createdAt: clinic.createdAt || null,
      updatedAt: clinic.updatedAt || null,
      
      // Tipo para el JSON
      type: 'HealthCenter',
      display_name: clinic.name,
      type_description: 'Centro Médico'
    }));
    
    console.log('✅ Centros médicos disponibles cargados:', healthCenters.length);
    setAvailableHealthCenters(healthCenters);
    return healthCenters;
    
  } catch (error) {
    console.error('❌ Error cargando centros médicos:', error);
    setAvailableHealthCenters([]);
    return [];
  } finally {
    setLoadingPayers(false);
  }
};

// ============================================
// 🔧 FUNCIÓN AUXILIAR: OBTENER PAGADORES SEGÚN TIPO
// ============================================
const getPayersByType = (payerType, currentPatientId = null) => {
  switch (payerType) {
    case 'Paciente':
    case 'User':
      if (availablePatients.length === 0 && currentPatientId) {
        fetchAvailablePatients(currentPatientId);
      }
      return availablePatients;
      
    case 'Aseguradora/Empresa':
    case 'Company':
      if (availableInsurers.length === 0) {
        fetchAvailableInsurers();
      }
      return availableInsurers;
      
    case 'Centro Médico':
    case 'HealthCenter':
      if (availableHealthCenters.length === 0) {
        fetchAvailableHealthCenters();
      }
      return availableHealthCenters;
      
    default:
      console.warn('⚠️ Tipo de pagador desconocido:', payerType);
      return [];
  }
};

// ============================================
// 🔧 FUNCIÓN AUXILIAR: CONSTRUIR PAYER_SNAPSHOT COMPLETO
// ============================================
const buildPayerSnapshot = (payer, payerType) => {
  if (!payer) return null;
  
  const baseSnapshot = {
    name: payer.name || payer.fullName || '',
    display_name: payer.display_name || payer.name || '',
    type_description: payer.type_description || ''
  };
  
  switch (payerType) {
    case 'User':
    case 'Paciente':
      return {
        ...baseSnapshot,
        address: {
          street: payer.address || '',
          city: payer.city || '',
          state: payer.state || '',
          zipcode: payer.zipcode || ''
        },
        contact: {
          phone: payer.phone || '',
          email: payer.email || ''
        },
        center_info: {
          insurance_contracts: payer.insurance ? [{
            company: payer.insuranceCompany || '',
            number: payer.insuranceNumber || '',
            expiration: payer.insuranceExpirationDate || null
          }] : []
        }
      };
      
    case 'Company':
    case 'Aseguradora/Empresa':
      return {
        ...baseSnapshot,
        cif: payer.cif || '',
        address: {
          street: payer.address || '',
          city: payer.city || '',
          state: payer.state || '',
          zipcode: payer.zipcode || ''
        },
        contact: {
          phone: payer.phone || '',
          email: payer.email || '',
          website: payer.website || ''
        },
        center_info: {
          insurance_contracts: [],
          insurance_types: payer.medicalExtension?.insurance || [],
          specialties: payer.medicalExtension?.specialties || [],
          facilities: payer.medicalExtension?.facilities || []
        }
      };
      
    case 'HealthCenter':
    case 'Centro Médico':
      return {
        ...baseSnapshot,
        cif: payer.cif || '',
        address: {
          street: payer.address || '',
          city: payer.city || '',
          state: payer.state || '',
          zipcode: payer.zipcode || ''
        },
        contact: {
          phone: payer.phone || '',
          email: payer.email || '',
          website: payer.website || ''
        },
        center_info: {
          insurance_contracts: [],
          services: payer.services || [],
          specialties: payer.specialties || [],
          departments: payer.departments || []
        }
      };
      
    default:
      return baseSnapshot;
  }
};

// ============================================
// 🔧 FUNCIÓN AUXILIAR: CONSTRUIR PARTNER_PAYLOAD COMPLETO
// ============================================
const buildPartnerPayload = (payer, payerType) => {
  if (!payer) return null;
  
  const basePayload = {
    name: payer.name || payer.fullName || '',
    email: payer.email || null,
    phone: payer.phone || null,
    street: payer.address || null,
    city: payer.city || null,
    zip: payer.zipcode || null,
    country_id: 'ES',
    x_medflow_payer_id: payer._id,
    x_medflow_payer_type: payerType === 'Paciente' ? 'User' : 
                          payerType === 'Aseguradora/Empresa' ? 'Company' : 
                          'HealthCenter'
  };
  
  // Para User: VAT es null // USAREMOS EL _id DEL USUARIO
  // Para Company/HealthCenter: VAT es el CIF
  if (payerType === 'User' || payerType === 'Paciente') {
    basePayload.vat = payer._id;
  } else {
    basePayload.vat = payer.cif || null;
  }
  
  return basePayload;
};
 
  const getActivityPricing = (activityKey) => {
    return activityPricing.get(activityKey) || {
      unit_price: 75,
      qty: 1,
      discount: 0,
      tax_profile: 'EXENTO',
      tax_rate: 0,
      notes: ''
    };
  };

  const handleActivityPricingChange = (activityKey, field, value) => {
    const currentPricing = getActivityPricing(activityKey);
    const newPricing = { ...currentPricing, [field]: value };
    
    // Actualizar tax_rate según tax_profile
    if (field === 'tax_profile') {
      newPricing.tax_rate = value === 'IVA21' ? 21 : 0;
    }
    
    const newMap = new Map(activityPricing);
    newMap.set(activityKey, newPricing);
    setActivityPricing(newMap);
  };

  const calculateActivityTotal = (pricing) => {
    const subtotal = (pricing.unit_price * pricing.qty) - pricing.discount;
    const taxAmount = subtotal * (pricing.tax_rate / 100);
    return subtotal + taxAmount;
  };

  const applyBulkPricing = () => {
    const defaultPrice = prompt('Ingrese el precio unitario a aplicar a todas las actividades:', '75');
    if (defaultPrice === null) return;
    
    const price = parseFloat(defaultPrice);
    if (isNaN(price) || price < 0) {
      alert('Precio inválido');
      return;
    }

    const newMap = new Map(activityPricing);
    selectedActivities.forEach(activity => {
      const key = `${activity.element_id}_${activity.activity_type}`;
      const current = getActivityPricing(key);
      newMap.set(key, { ...current, unit_price: price });
    });
    setActivityPricing(newMap);
  };

  /**
 * 🚀 NUEVA FUNCIÓN: Exportar a Odoo desde el Wizard
 */
const handleExportToOdooFromWizard = async (salesOrderIds) => {
  if (!salesOrderIds || salesOrderIds.length === 0) {
    alert('No hay facturas para exportar');
    return;
  }

  setIsExportingToOdoo(true);
  setExportResults(null);

  try {
    console.log('🚀 Exportando a Odoo desde wizard:', salesOrderIds);

    const invoicesToExport = createdInvoices.created_sales_orders
      .filter(so => salesOrderIds.includes(so.sales_order_id))
      .map(so => so.odoo_json);

    console.log('📦 Facturas a exportar:', invoicesToExport.length);

    const completedInvoices = invoicesToExport.map(invoice => 
      completeInvoiceJSON(invoice, patientsData, clinicsData)
    );

    console.log('✅ Facturas completadas con datos:', completedInvoices);

    const response = await fetch(`${API_BASE_URL}/billing/export-to-odoo`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        sales_order_ids: salesOrderIds,
        completed_invoices: completedInvoices
      })
    });

    const result = await response.json();

    if (result.error) {
      throw new Error(result.error.message);
    }

    setExportResults(result.data);
    console.log('✅ Exportación completada:', result.data);

    // ✅ NUEVO: Refrescar datos inmediatamente después de exportar
    if (refreshData) {
      console.log('🔄 Refrescando datos después de exportar a Odoo...');
      setTimeout(() => {
        refreshData();
      }, 1000); // Esperar 1 segundo para que el backend actualice
    }

  } catch (error) {
    console.error('❌ Error exportando a Odoo:', error);
    setExportResults({
      exported: [],
      errors: [{
        sales_order_id: 'ALL',
        error: error.message
      }]
    });
  } finally {
    setIsExportingToOdoo(false);
  }
};

  // 🔧 FIX: Función mejorada para manejar splits
  const handleSplitChange = (activityKey, splits, isValid) => {
    console.log('🔄 handleSplitChange llamado:', { 
      activityKey, 
      splits_count: splits?.length, 
      isValid,
      splits_data: splits
    });
    
    setActivitySplits(prevSplits => {
      const newMap = new Map(prevSplits);
      newMap.set(activityKey, {
        splits: splits || [],
        isValid: isValid === true
      });
      
      console.log('💾 Activity splits actualizado para', activityKey, ':', {
        splits_count: splits?.length,
        isValid: isValid === true
      });
      
      return newMap;
    });
  };

  // 🔧 Validación mejorada de splits
const areAllSplitsValid = () => {
  console.log('🔍 Validando splits de todas las actividades...');
  
  let allValid = true;
  const invalidActivities = [];
  
  for (let activity of selectedActivities) {
    const key = `${activity.element_id}_${activity.activity_type}`;
    const splitConfig = activitySplits.get(key);
    
    // Validación 1: Debe existir configuración
    if (!splitConfig) {
      console.warn(`❌ Split inexistente para ${key}`);
      invalidActivities.push({
        activity: activity.description,
        issue: 'No hay configuración de splits'
      });
      allValid = false;
      continue;
    }

    // Validación 2: Debe tener al menos un split
    if (!splitConfig.splits || splitConfig.splits.length === 0) {
      console.warn(`❌ Split vacío para ${key}`);
      invalidActivities.push({
        activity: activity.description,
        issue: 'No hay splits configurados'
      });
      allValid = false;
      continue;
    }

    // Validación 3: Verificar que cada split es válido
    for (let i = 0; i < splitConfig.splits.length; i++) {
      const split = splitConfig.splits[i];
      
      // Validar payer_type
      if (!['User', 'Company', 'HealthCenter'].includes(split.payer_type)) {
        invalidActivities.push({
          activity: activity.description,
          issue: `Split ${i + 1}: payer_type inválido: ${split.payer_type}`
        });
        allValid = false;
      }

      // Validar payer_id
      if (!split.payer_id) {
        invalidActivities.push({
          activity: activity.description,
          issue: `Split ${i + 1}: sin payer_id`
        });
        allValid = false;
      }

      // Validar percent
      if (typeof split.percent !== 'number' || split.percent <= 0) {
        invalidActivities.push({
          activity: activity.description,
          issue: `Split ${i + 1}: percent inválido (${split.percent})`
        });
        allValid = false;
      }
    }

    // Validación 4: Verificar que los porcentajes sumen 100%
    const totalPercent = splitConfig.splits.reduce((sum, s) => sum + (s.percent || 0), 0);
    if (Math.abs(totalPercent - 100) > 0.01) {
      console.warn(`❌ Splits para ${key} suman ${totalPercent}% en lugar de 100%`);
      invalidActivities.push({
        activity: activity.description,
        issue: `Splits suman ${totalPercent.toFixed(2)}% en lugar de 100%`
      });
      allValid = false;
    }

    // Validación 5: isValid debe ser true
    if (splitConfig.isValid !== true) {
      console.warn(`❌ Split inválido para ${key}: isValid = ${splitConfig.isValid}`);
      invalidActivities.push({
        activity: activity.description,
        issue: 'Configuración de split marcada como inválida'
      });
      allValid = false;
    }
  }
  
  if (allValid) {
    console.log('✅ Todos los splits válidos y correctos');
  } else {
    console.error('❌ Problemas encontrados:', invalidActivities);
  }
  
  return allValid;
};

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    let total = 0;

    selectedActivities.forEach(activity => {
      const key = `${activity.element_id}_${activity.activity_type}`;
      const pricing = getActivityPricing(key);
      const activitySubtotal = (pricing.unit_price * pricing.qty) - pricing.discount;
      const activityTax = activitySubtotal * (pricing.tax_rate / 100);
      
      subtotal += activitySubtotal;
      totalTax += activityTax;
      total += activitySubtotal + activityTax;
    });
    return { subtotal, totalTax, total };
  };

  const handleNextStep = () => {
    if (activeStep === 0) {
      // Validar que todos los splits estén configurados
      if (!areAllSplitsValid()) {
        alert('Por favor configura los copagos correctamente para todas las actividades');
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBackStep = () => {
    setActiveStep((prev) => prev - 1);
  };

const handleCreateInvoices = async () => {
  setIsCreating(true);
  setInvoiceError(null);
  
  try {
    console.log('🚀 Validando datos antes de crear facturas...');
    
    // Validar estructura de actividades seleccionadas
    console.log('📋 Actividades seleccionadas:', selectedActivities.map(a => ({
      element_id: a.element_id,
      activity_type: a.activity_type,
      description: a.description
    })));
    
    // Validar usando element_id
    const missingFields = selectedActivities.filter(a => !a.element_id || !a.activity_type);
    if (missingFields.length > 0) {
      console.error('❌ Actividades con campos faltantes:', missingFields);
      throw new Error(
        `${missingFields.length} actividad(es) no tienen element_id o activity_type. ` +
        `Verifica que las actividades vengan correctamente del componente padre.`
      );
    }
    
    // Validar splits válidos
    if (!areAllSplitsValid()) {
      throw new Error('No todos los splits están configurados correctamente o sumen 100%');
    }
    
    // Validar pricing
    selectedActivities.forEach(activity => {
      const key = `${activity.element_id}_${activity.activity_type}`;
      const pricing = getActivityPricing(key);
      if (!pricing || pricing.unit_price <= 0) {
        throw new Error(`Actividad ${activity.description} no tiene precio válido`);
      }
    });
    
    console.log('✅ Validaciones pasadas, construyendo payload...');
    
    // ✅ CONSTRUIR PAYLOAD CON PRODUCT_INFO COMPLETO
    const originalActivities = selectedActivities.map(activity => {
      const key = `${activity.element_id}_${activity.activity_type}`;
      const pricing = getActivityPricing(key);
      const splitConfig = activitySplits.get(key);
      
      if (!splitConfig || !splitConfig.splits || splitConfig.splits.length === 0) {
        throw new Error(`No hay splits configurados para: ${activity.description}`);
      }

      const activityTotal = calculateActivityTotal(pricing);
      const splitTotalPercent = splitConfig.splits.reduce((sum, s) => sum + (s.percent || 0), 0);
      
      if (Math.abs(splitTotalPercent - 100) > 0.01) {
        throw new Error(
          `Los splits de "${activity.description}" suman ${splitTotalPercent.toFixed(2)}% en lugar de 100%`
        );
      }

      // ✅ ENRIQUECER SPLITS
      const enrichedSplits = splitConfig.splits.map((split, splitIndex) => {
        console.log(`Procesando split ${splitIndex + 1} para ${activity.description}:`, {
          payer_id: split.payer_id,
          payer_type: split.payer_type,
          percent: split.percent,
          has_snapshot: !!split.payer_snapshot
        });
        
        if (!['User', 'Company', 'HealthCenter'].includes(split.payer_type)) {
          throw new Error(
            `Split inválido en "${activity.description}": ` +
            `payer_type debe ser User, Company o HealthCenter. Recibido: ${split.payer_type}`
          );
        }

        if (!split.payer_id) {
          throw new Error(`Split inválido en "${activity.description}": payer_id es requerido`);
        }

        if (typeof split.percent !== 'number' || split.percent <= 0) {
          throw new Error(
            `Split inválido en "${activity.description}": ` +
            `percent debe ser un número > 0. Recibido: ${split.percent}`
          );
        }

        let payerSnapshot = split.payer_snapshot || {};

        if (!payerSnapshot.name) {
          if (split.payer_type === 'User' && split.payer_id === activity.patient_id) {
            payerSnapshot = {
              name: activity.patient_name,
              display_name: activity.patient_name,
              type_description: 'Paciente',
              vat: activity.patient_id, 
              contact: {
                phone: activity.patient_phone || '',
                email: activity.patient_email || ''
              },
              address: activity.patient_address || {}
            };
            console.log(`✅ Patient payer snapshot creado: ${payerSnapshot.name}`);
          } else if (split.payer_type === 'Company') {
            const company = availableCompanies.find(c => c.id === split.payer_id);
            if (company) {
              payerSnapshot = {
                name: company.name,
                display_name: company.display_name || company.name,
                type_description: 'Compañía',
                cif: company.cif || '',
                vat: company.cif || '',
                contact: {
                  phone: company.phone || '',
                  email: company.email || ''
                },
                address: company.address || {}
              };
              console.log(`✅ Company payer snapshot creado: ${payerSnapshot.name}`);
            } else {
              throw new Error(
                `Compañía ${split.payer_id} no encontrada. ` +
                `Compañías disponibles: ${availableCompanies.map(c => c.id).join(', ')}`
              );
            }
          } else if (split.payer_type === 'HealthCenter') {
            payerSnapshot = {
              name: 'Centro de Salud',
              display_name: 'Centro de Salud',
              type_description: 'Centro de Salud'
            };
          } else {
            throw new Error(
              `No se puede crear payer_snapshot: ` +
              `payer_type=${split.payer_type}, payer_id=${split.payer_id}`
            );
          }
        }
        
        return {
          payer_id: split.payer_id,
          payer_type: split.payer_type,
          percent: split.percent,
          max_amount: split.max_amount || null,
          payer_snapshot: payerSnapshot,
          vat: split.payer_type === 'User' ? split.payer_id : payerSnapshot.cif || null 

        };
      });

      // ✅ CONSTRUIR PRODUCT_INFO - CORREGIDO DEFINITIVO
      let productInfo = null;

      // Verificar si la actividad tiene información de productos
      if (activity.has_product_info || 
          activity.producto_cita_id || 
          activity.producto_cita_name ||
          activity.despacho_medico_id ||
          activity.despacho_medico_name) {
        
        productInfo = {};

        // === PRODUCTO DE LA CITA ===
        if (activity.producto_cita_id || activity.producto_cita_name) {
          productInfo.producto_cita_id = activity.producto_cita_id || null;
          productInfo.producto_cita_name = activity.producto_cita_name || '';
          productInfo.producto_cita_code = activity.producto_cita_code || '';
          productInfo.producto_cita_price = parseFloat(activity.producto_cita_price || 0);
        }

        // === DESPACHO MÉDICO ===
        if (activity.despacho_medico_id || activity.despacho_medico_name) {
          productInfo.despacho_medico_id = activity.despacho_medico_id || null;
          productInfo.despacho_medico_name = activity.despacho_medico_name || '';
          productInfo.despacho_medico_code = activity.despacho_medico_code || '';
        }

        // === NOTAS ADICIONALES ===
        productInfo.product_notes = pricing.notes || '';

        console.log('📦 Product info construido para', activity.description, ':', productInfo);
      } else {
        console.log('ℹ️ Actividad sin información de productos:', activity.description);
      }

      // ✅ CONSTRUIR OBJETO FINAL
      const originalActivity = {
        element_id: activity.element_id,
        source_type: activity.activity_type,
        
        patient_id: activity.patient_id,
        patient_name: activity.patient_name,
        patient_vat: activity.patient_id,
        doctor_id: activity.doctor_id || null,
        doctor_name: activity.doctor_name || '',
        center_id: activity.center_id || null,
        
        description: activity.description,
        performed_at: activity.performed_at_raw || activity.performed_at || new Date().toISOString(),
        
        unit_price: pricing.unit_price,
        quantity: pricing.qty,
        discount: pricing.discount || 0,
        tax_profile: pricing.tax_profile || 'EXENTO',
        tax_rate: pricing.tax_rate || 0,
        notes: pricing.notes || '',
        
        payment_splits: enrichedSplits,

        patient_vat: activity.patient_id, // ✅ CRÍTICO: Enviar el VAT del paciente
        
        // ✅ CRÍTICO: Solo incluir si productInfo no es null
        ...(productInfo && { product_info: productInfo })
      };

      console.log(`✅ Actividad construida:`, {
        element_id: originalActivity.element_id,
        description: originalActivity.description,
        has_product_info: !!originalActivity.product_info,
        product_info_keys: originalActivity.product_info ? Object.keys(originalActivity.product_info) : []
      });

      return originalActivity;
    });

    const invoicePayload = {
      original_activities: originalActivities,
      grouping_mode: invoiceData.grouping_mode,
      additional_notes: invoiceData.additional_notes,
      auto_generate_split: false,
      default_tax_profile: 'EXENTO'
    };
    
    console.log('📦 Payload construido completamente:', {
      activities_count: originalActivities.length,
      grouping_mode: invoicePayload.grouping_mode,
      first_activity: {
        element_id: originalActivities[0]?.element_id,
        source_type: originalActivities[0]?.source_type,
        has_splits: originalActivities[0]?.payment_splits?.length > 0,
        has_product_info: !!originalActivities[0]?.product_info,
        product_info: originalActivities[0]?.product_info
      }
    });
    
    // DEBUGGING: Payload completo
    console.log('🚀 === PAYLOAD COMPLETO A ENVIAR ===');
    console.log('Total actividades:', originalActivities.length);
    originalActivities.forEach((act, idx) => {
      console.log(`\n📦 Actividad ${idx + 1}:`);
      console.log('  - element_id:', act.element_id);
      console.log('  - description:', act.description);
      console.log('  - product_info:', JSON.stringify(act.product_info, null, 2));
      console.log('  - unit_price:', act.unit_price);
      console.log('  - splits:', act.payment_splits?.length);
    });
    console.log('=================================\n');
    
    // LLAMAR AL BACKEND
    console.log('🌐 Enviando request al backend...');
    const response = await PatientBillingService.createInvoiceFromOriginalActivities(invoicePayload);
    
    console.log('📡 Respuesta del backend:', response);
    
    if (response.error) {
      console.error('❌ Error detallado del backend:', {
        code: response.error.code,
        message: response.error.message,
        details: response.error.details
      });
      throw new Error(response.error.message || 'Error en la respuesta del servidor');
    }
    
    setCreatedInvoices(response.data);
    setActiveStep(2);
    console.log('✅ Facturas creadas exitosamente');
    
  } catch (error) {
    console.error('❌ Error creando facturas:', error);
    setInvoiceError(error.message || 'Error desconocido al crear facturas');
    setActiveStep(2);
  } finally {
    setIsCreating(false);
  }
};

  const handleViewJson = (odooJson) => {
  const jsonStr = JSON.stringify(odooJson, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
  window.URL.revokeObjectURL(url);
};

const handleDownloadJson = (odooJson) => {
  const jsonStr = JSON.stringify(odooJson, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `invoice_odoo_${odooJson.x_medflow_sales_order_id || Date.now()}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const handleCopyJson = async (odooJson) => {
  try {
    const jsonStr = JSON.stringify(odooJson, null, 2);
    await navigator.clipboard.writeText(jsonStr);
    alert('JSON copiado al portapapeles');
  } catch (error) {
    console.error('Error copiando JSON:', error);
    alert('Error al copiar JSON');
  }
};

  const handleFinishInvoicing = () => {
    handleCloseInvoiceOffcanvas();
    
    if (refreshData) {
    console.log('🔄 Refrescando datos después de crear facturas...');
    refreshData();
  } else {
    console.log('🔄 Recargando página...');
    window.location.reload();
  }
};

  /**
 * 🚀 EXPORTAR A ODOO
 */
const handleExportToOdoo = async (salesOrderIds) => {
  if (!salesOrderIds || salesOrderIds.length === 0) {
    alert('No hay facturas para exportar');
    return;
  }

  if (!window.confirm(`¿Exportar ${salesOrderIds.length} factura(s) a Odoo?`)) {
    return;
  }

  setIsCreating(true);

  try {
    console.log('🚀 Exportando a Odoo:', salesOrderIds);

    // 🆕 Asegurarse de tener datos actualizados
    if (patientsData.length === 0 || clinicsData.length === 0) {
      await fetchAvailableHealthCenters();
    }

    const response = await fetch(`${API_BASE_URL}/billing/export-to-odoo`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        sales_order_ids: salesOrderIds
      })
    });

    const result = await response.json();

    if (result.error) {
      alert(`Error: ${result.error.message}`);
      return;
    }

    const { exported, errors, summary } = result.data;

    let message = `✅ Exportación completada:\n`;
    message += `- Exitosas: ${summary.exported}\n`;
    message += `- Errores: ${summary.errors}\n\n`;

    if (exported.length > 0) {
      message += `Facturas exportadas:\n`;
      exported.forEach(e => {
        message += `- SO ${e.sales_order_id} → Odoo SO${e.odoo_order_id}\n`;
      });
    }

    if (errors.length > 0) {
      message += `\n❌ Errores:\n`;
      errors.forEach(e => {
        message += `- ${e.sales_order_id}: ${e.error}\n`;
      });
    }

    alert(message);

    if (refreshData) {
      refreshData();
    }

  } catch (error) {
    console.error('❌ Error exportando a Odoo:', error);
    alert(`Error al exportar a Odoo: ${error.message}`);
  } finally {
    setIsCreating(false);
  }
};

  // 🔧 FIX: Función para construir la lista de pagadores disponibles
  const getAvailablePayersForActivity = (activity) => {
  console.log('🔍 Construyendo lista de pagadores para actividad:', activity);
  
  const payers = [];
  
  // ✅ Siempre agregar el paciente como primer pagador CON TIPO CORRECTO
  if (activity.patient_id && activity.patient_name) {
    payers.push({
      id: activity.patient_id,
      type: 'User', // 🔧 FIX: Usar 'User' en lugar de 'patient'
      name: activity.patient_name,
      display_name: activity.patient_name,
      email: activity.patient_email || '', // 🔧 Agregar email si está disponible
      phone: activity.patient_phone || ''  // 🔧 Agregar teléfono si está disponible
    });
  }
  
  // 🔧 Agregar compañías con el tipo correcto "Company"
  availableCompanies.forEach(company => {
    payers.push({
      id: company.id,
      type: 'Company',
      name: company.name,
      display_name: company.display_name || company.name,
      cif: company.cif
    });
  });
  
  console.log('📋 Pagadores disponibles:', payers);
  return payers;
};

  // Renderizar el contenido del offcanvas según el paso activo
  const renderOffcanvasStep = () => {
    const totals = calculateTotals();
    
    switch (activeStep) {
      case 0: // Configurar precios y splits
        return (
          <div className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>
                <FaEuroSign className="me-2" />
                Configuración de Precios y Pagadores
              </h6>
              <div>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={applyBulkPricing}
                >
                  <BsCalculator className="me-1" />
                  Precio Masivo
                </button>
              </div>
            </div>

            <Alert severity="info" sx={{ mb: 2 }}>
              Configura el precio y los pagadores para cada actividad. Los copagos deben sumar 100%.
            </Alert>

            {/* Activities Pricing */}
            <div className="row">
              {selectedActivities.map((activity) => {
                const activityKey = `${activity.element_id}_${activity.activity_type}`;
                const pricing = getActivityPricing(activityKey);
                const total = calculateActivityTotal(pricing);
                const splitConfig = activitySplits.get(activityKey);
                const isEditing = editingPricing === activityKey;

                return (
                  <div key={activityKey} className="col-12 mb-4">
                    <div className="card">
                      <div className="card-header bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{activity.description}</strong>
                            <div>
                              <small className="text-muted">
                                <span className="badge bg-secondary me-1">{activity.activity_type}</span>
                                <span className="badge bg-light text-dark me-1">Paciente: {activity.patient_name}</span>
                                {activity.performed_at && (
                                  <span>{new Date(activity.performed_at).toLocaleDateString('es-ES')}</span>
                                )}
                              </small>
                            </div>
                            {/* ✅ NUEVA SECCIÓN: Mostrar información de productos */}
              {activity.has_product_info && (
                <div className="mt-2">
                  <small className="d-block">
                    {activity.producto_cita_name && (
                      <span className="badge bg-info me-1">
                        📦 {activity.producto_cita_name}
                        {activity.producto_cita_price > 0 && 
                          ` - ${new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(activity.producto_cita_price)}`
                        }
                      </span>
                    )}
                    {activity.despacho_medico_name && (
                      <span className="badge bg-secondary me-1">
                        🏥 Despacho: {activity.despacho_medico_name}
                      </span>
                    )}
                  </small>
                </div>
              )}

                          </div>
                          <div className="text-end">
                            <div className="h5 mb-0 text-success">€{total.toFixed(2)}</div>
                            <small className={`badge ${splitConfig?.isValid ? 'bg-success' : 'bg-warning'}`}>
                              {splitConfig?.isValid ? 'Split válido' : 'Configurar split'}
                            </small>
                            <button
                              className={`btn btn-sm ms-2 ${isEditing ? 'btn-success' : 'btn-outline-secondary'}`}
                              onClick={() => setEditingPricing(isEditing ? null : activityKey)}
  
  >
                              {isEditing ? <FaSave /> : <FaEdit />}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="card-body">
                        {/* Pricing Configuration */}
                        {isEditing && (
  <div className="bg-light p-3 rounded mb-3">
    <h6 className="text-primary mb-3">Configurar Precio</h6>
    
{/* 🆕 SELECTORES DE PRECIOS */}
<div className="row g-3 mb-3 pb-3 border-bottom">
  <div className="col-12">
    <label className="form-label fw-bold text-primary">
      📦 Cargar Precio del Catálogo de Productos
    </label>
    <div className="d-flex gap-2 align-items-center">
      <select
        className="form-select"
        style={{ flex: 1 }}
        value={selectedProductPricelist.get(activityKey) || ""}
        onChange={(e) => {
          if (e.target.value) {
            handleLoadProductPrice(activity, activityKey, e.target.value);
          }
        }}
        disabled={loadingPricelists.get(`${activityKey}_product`)}
      >
        <option value="">-- Seleccionar producto del catálogo --</option>
        {(productPricelists.get(activityKey) || []).map((product) => {
          const price = parseFloat(
            product.priceExcludingVAT?.$numberDecimal || 
            product.priceExcludingVAT || 
            product.price?.$numberDecimal ||
            product.price ||
            0
          ).toFixed(2);
          
          const productName = product.productName || product.name || 'Producto sin nombre';
          const category = product.category || 'Sin categoría';
          const type = product.type || '';
          
          return (
            <option key={product._id} value={product._id}>
              📦 {productName} | 🏷️ {category} {type ? `| ${type}` : ''} | {price} €
            </option>
          );
        })}
      </select>
      
      <button
        className="btn btn-outline-info"
        onClick={() => fetchProductsForActivity(activity, activityKey)}
        disabled={loadingPricelists.get(`${activityKey}_product`)}
        title="Cargar productos del catálogo"
      >
        {loadingPricelists.get(`${activityKey}_product`) ? '⏳' : '🔄'}
      </button>
      
      {/* Caja negra - Precio del producto */}
      <div className="input-group" style={{ width: '150px' }}>
        <input
          type="number"
          step="0.01"
          className="form-control text-center fw-bold"
          style={{ fontSize: '14px' }}
          value={displayedProductPrices.get(activityKey)?.price || ''}
          onChange={(e) => {
            const newPrice = parseFloat(e.target.value) || 0;
            const newDisplayedPrices = new Map(displayedProductPrices);
            newDisplayedPrices.set(activityKey, { 
              price: newPrice, 
              currency: '€' 
            });
            setDisplayedProductPrices(newDisplayedPrices);
          }}
          placeholder="0.00"
          readOnly={!displayedProductPrices.has(activityKey)}
        />
        <span className="input-group-text">
          {displayedProductPrices.get(activityKey)?.currency || '€'}
        </span>
      </div>
    </div>
  </div>
  <div className="col-md-6">
    <label className="form-label fw-bold">
      📋 Cargar Precio de Cliente
    </label>
    <div className="d-flex gap-2 align-items-center">
      {/* SELECTOR DE CLIENTE */}
<select
  className="form-select"
  style={{ flex: 1 }}
  value={selectedCustomerPricelist.get(activityKey) || ""}
  onChange={(e) => {
    if (e.target.value) {
      handleLoadCustomerPrice(activity, activityKey, e.target.value);
    }
  }}
  disabled={loadingPricelists.get(`${activityKey}_customer`)}
>
  <option value="">-- Seleccionar tarifa de cliente --</option>
  {(customerPricelists.get(activityKey) || []).map((pricelist) => {
    const price = parseFloat(
      pricelist.price.$numberDecimal || pricelist.price.toString()
    ).toFixed(2);
    const payerName = pricelist.companyid?.name || 
                     pricelist.userid?.name || 
                     'Desconocido';
    const payerType = pricelist.companyid ? '🏢' : '👤';
    
    const productName = pricelist.productid?.name || 
                       pricelist.productid?.productName || 
                       'Producto sin nombre';
    
    return (
      <option key={pricelist._id} value={pricelist._id}>
        {payerType} {payerName} | 📦 {productName} | {price} {pricelist.currency || '€'} 
        (Mín: {pricelist.minQuantity || 1})
      </option>
    );
  })}
</select>
      
      <button
        className="btn btn-outline-primary"
        onClick={() => fetchPricelistsForActivity(activity, activityKey, 'customer')}
        disabled={loadingPricelists.get(`${activityKey}_customer`)}
        title="Recargar tarifas de clientes"
      >
        {loadingPricelists.get(`${activityKey}_customer`) ? '⏳' : '🔄'}
      </button>
      
      {/* 🆕 CAJA NEGRA - MUESTRA EL PRECIO DEL PRICELIST SELECCIONADO */}
      <div className="input-group" style={{ width: '150px' }}>
        <input
          type="number"
          step="0.01"
          className="form-control text-center fw-bold"
          style={{           
            
            fontSize: '14px'
          }}
          value={displayedCustomerPrices.get(activityKey)?.price || ''}
          onChange={(e) => {
            const newPrice = parseFloat(e.target.value) || 0;
            const newDisplayedPrices = new Map(displayedCustomerPrices);
            newDisplayedPrices.set(activityKey, { 
              price: newPrice, 
              currency: displayedCustomerPrices.get(activityKey)?.currency || '€' 
            });
            setDisplayedCustomerPrices(newDisplayedPrices);
          }}
          placeholder="0.00"
          readOnly={!displayedCustomerPrices.has(activityKey)}
        />
        <span className="input-group-text">
          {displayedCustomerPrices.get(activityKey)?.currency || '€'}
        </span>
      </div>
    </div>    
    
  </div>
  
  <div className="col-md-6">
    <label className="form-label fw-bold">
      🏭 Cargar Precio de Proveedor
    </label>
    <div className="d-flex gap-2 align-items-center">
      {/* SELECTOR DE PROVEEDOR */}
<select
  className="form-select"
  style={{ flex: 1 }}
  value={selectedSupplierPricelist.get(activityKey) || ""}
  onChange={(e) => {
    if (e.target.value) {
      handleLoadSupplierPrice(activity, activityKey, e.target.value);
    }
  }}
  disabled={loadingPricelists.get(`${activityKey}_supplier`)}
>
  <option value="">-- Seleccionar tarifa de proveedor --</option>
  {(supplierPricelists.get(activityKey) || []).map((pricelist) => {
    const price = parseFloat(
      pricelist.price.$numberDecimal || pricelist.price.toString()
    ).toFixed(2);
    const supplierName = pricelist.supplierid?.name || 
                        pricelist.userid?.name || 
                        'Desconocido';
    const supplierType = pricelist.supplierid ? '🏢' : '👤';
    
    const productName = pricelist.productid?.name || 
                       pricelist.productid?.productName || 
                       'Producto sin nombre';
    
    return (
      <option key={pricelist._id} value={pricelist._id}>
        {supplierType} {supplierName} | 📦 {productName} | {price} {pricelist.currency || '€'} 
        (Mín: {pricelist.minQuantity || 1})
      </option>
    );
  })}
</select>
      
      <button
        className="btn btn-outline-secondary"
        onClick={() => fetchPricelistsForActivity(activity, activityKey, 'supplier')}
        disabled={loadingPricelists.get(`${activityKey}_supplier`)}
        title="Recargar tarifas de proveedores"
      >
        {loadingPricelists.get(`${activityKey}_supplier`) ? '⏳' : '🔄'}
      </button>
      
      {/* 🆕 CAJA NEGRA - MUESTRA EL PRECIO DEL PRICELIST SELECCIONADO */}
      <div className="input-group" style={{ width: '150px' }}>
        <input
          type="number"
          step="0.01"
          className="form-control text-center fw-bold"
          style={{
            fontSize: '14px'
          }}
          value={displayedSupplierPrices.get(activityKey)?.price || ''}
          onChange={(e) => {
            const newPrice = parseFloat(e.target.value) || 0;
            const newDisplayedPrices = new Map(displayedSupplierPrices);
            newDisplayedPrices.set(activityKey, { 
              price: newPrice, 
              currency: displayedSupplierPrices.get(activityKey)?.currency || '€' 
            });
            setDisplayedSupplierPrices(newDisplayedPrices);
          }}
          placeholder="0.00"
          readOnly={!displayedSupplierPrices.has(activityKey)}
        />
        <span className="input-group-text">
          {displayedSupplierPrices.get(activityKey)?.currency || '€'}
        </span>
      </div>
    </div>
    
    
  </div>
</div>

    {/* CAMPOS DE PRECIO ORIGINALES */}
    <div className="row g-3">
      <div className="col-md-3">
        <label className="form-label">Precio Unit. (€)</label>
        <input
          type="number"
          step="0.01"
          className="form-control"
          value={pricing.unit_price}
          onChange={(e) => handleActivityPricingChange(
            activityKey, 
            'unit_price', 
            parseFloat(e.target.value) || 0
          )}
        />
      </div>
                              <div className="col-md-2">
                                <label className="form-label">Cantidad</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={pricing.qty}
                                  onChange={(e) => handleActivityPricingChange(
                                    activityKey, 
                                    'qty', 
                                    parseInt(e.target.value) || 1
                                  )}
                                />
                              </div>
                              <div className="col-md-3">
                                <label className="form-label">Descuento (€)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  value={pricing.discount}
                                  onChange={(e) => handleActivityPricingChange(
                                    activityKey, 
                                    'discount', 
                                    parseFloat(e.target.value) || 0
                                  )}
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label">IVA</label>
                                <select
                                  className="form-select"
                                  value={pricing.tax_profile}
                                  onChange={(e) => handleActivityPricingChange(
                                    activityKey, 
                                    'tax_profile', 
                                    e.target.value
                                  )}
                                >
                                  <option value="EXENTO">Exento</option>
                                  <option value="IVA21">IVA 21%</option>
                                </select>
                              </div>
                              <div className="col-md-2">
                                <label className="form-label">&nbsp;</label>
                                <button
                                  className="btn btn-success w-100"
                                  onClick={() => setEditingPricing(null)}
                                >
                                  <FaSave className="me-1" />
                                  Guardar
                                </button>
                              </div>
                              <div className="col-12">
                                <label className="form-label">Notas</label>
                                <textarea
                                  className="form-control"
                                  rows="2"
                                  value={pricing.notes}
                                  onChange={(e) => handleActivityPricingChange(
                                    activityKey, 
                                    'notes', 
                                    e.target.value
                                  )}
                                  placeholder="Notas adicionales..."
                                />
                              </div>
                            </div>

                            {/* Calculation Preview */}
                            <div className="row mt-3 text-center">
  <div className="col-md-4">
    <div className="card bg-white">
      <div className="card-body py-2">
        <div className="h6 mb-0">
          {new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(((pricing.unit_price * pricing.qty) - pricing.discount))}
        </div>
        <small className="text-muted">Subtotal</small>
      </div>
    </div>
  </div>
  <div className="col-md-4">
    <div className="card bg-white">
      <div className="card-body py-2">
        <div className="h6 mb-0">
          {new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format((((pricing.unit_price * pricing.qty) - pricing.discount) * (pricing.tax_rate / 100)))}
        </div>
        <small className="text-muted">IVA ({pricing.tax_rate}%)</small>
      </div>
    </div>
  </div>
  <div className="col-md-4">
    <div className="card bg-success text-white">
      <div className="card-body py-2">
        <div className="h6 mb-0">
          {new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(total)}
        </div>
        <small>Total</small>
      </div>
    </div>
  </div>
</div>
                          </div>
                        )}

                        {/* 🔧 FIX: Split Configuration con información completa del paciente */}
                        <div className="border-top pt-3">
                          <h6 className="text-primary mb-3">
                            <FaPercent className="me-2" />
                            Configuración de Copagos
                          </h6>
                          <SplitConfiguration
  activity={{
    key: activityKey,
    patient_id: activity.patient_id,
    patient_name: activity.patient_name,
    patient_email: activity.patient_email,
    patient_phone: activity.patient_phone,
    patient_address: activity.patient_address,
    description: activity.description,
    total_amount: total
  }}
  availablePayers={getAvailablePayersForActivity(activity)}
  availablePatients={availablePatients}      // 🆕 CRÍTICO
  availableInsurers={availableInsurers}      // 🆕 CRÍTICO
  availableHealthCenters={availableHealthCenters}  // 🆕 CRÍTICO
  initialSplits={activitySplits.get(activityKey)?.splits}
  onSplitChange={(key, splits, isValid) => handleSplitChange(key, splits, isValid)}
/>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals Summary */}
            {selectedActivities.length > 0 && (
              <div className="card bg-light mb-4">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-8">
                      <h6>Resumen General</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <p className="mb-1"><strong>Actividades:</strong> {selectedActivities.length}</p>
                          <p className="mb-0"><strong>Splits válidos:</strong> {areAllSplitsValid() ? 'Sí ✓' : 'No ✗'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 text-end">
                      <div className="mb-1"><strong>Subtotal:</strong> €{totals.subtotal.toFixed(2)}</div>
                      <div className="mb-1"><strong>IVA:</strong> €{totals.totalTax.toFixed(2)}</div>
                      <div className="h4 text-success"><strong>Total:</strong> €{totals.total.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Validation Alert */}
            {!areAllSplitsValid() && selectedActivities.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Atención:</strong> Asegúrate de que todos los copagos estén configurados correctamente y sumen 100%
              </Alert>
            )}
          </div>
        );

      case 1: // Configuración de agrupación
        return (
          <Box sx={{ p: 4 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Modo de Agrupación</InputLabel>
              <label
                value={invoiceData.grouping_mode}
                onChange={(e) => setInvoiceData({ ...invoiceData, grouping_mode: e.target.value })}
                label="Modo de Agrupación"
              >
                <MenuItem value="PAYER_PATIENT">
                  Por Pagador y Paciente (Factura separada por cada combinación)
                </MenuItem>
                {/* <MenuItem value="PAYER_ONLY">
                  Solo por Pagador (Agrupar todos los pacientes del mismo pagador)
                </MenuItem>
                <MenuItem value="SINGLE_INVOICE">
                  Factura Única (Todas las actividades en una sola factura)
                </MenuItem> */}
              </label>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notas Adicionales"
              placeholder="Notas o comentarios adicionales para las facturas..."
              value={invoiceData.additional_notes}
              onChange={(e) => setInvoiceData({ ...invoiceData, additional_notes: e.target.value })}
            />

            {/* <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Modo seleccionado:</strong> {
                  invoiceData.grouping_mode === 'PAYER_PATIENT' ? 'Por Pagador y Paciente' :
                  invoiceData.grouping_mode === 'PAYER_ONLY' ? 'Solo por Pagador' :
                  'Factura Única'
                }
              </Typography>
            </Alert> */}

            {/* Resumen rápido */}
            <Card variant="outlined" sx={{ mt: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">Actividades:</Typography>
                    <Typography variant="h6">{selectedActivities.length}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">Pacientes:</Typography>
                    <Typography variant="h6">
                      {[...new Set(selectedActivities.map(act => act.patient_id))].length}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="textSecondary">Total:</Typography>
                    <Typography variant="h6" color="primary">
                      {BillingUtils.formatCurrency(totals.total)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );

      case 2: // Resultado
  return (
    <Box sx={{ p: 4 }}>
      {invoiceError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Error al Crear Facturas
          </Typography>
          <Typography variant="body2">
            {invoiceError}
          </Typography>
        </Alert>
      ) : createdInvoices ? (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              ¡Facturas Creadas Exitosamente!
            </Typography>
            <Typography variant="body2">
              Se han creado {createdInvoices.created_sales_orders?.length || 0} factura(s)
            </Typography>
          </Alert>

          {/* 🆕 BOTÓN DE EXPORTACIÓN A ODOO - MEJORADO */}
          <Box sx={{ mb: 3 }}>
            {isExportingToOdoo ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="textSecondary">
                  Exportando a Odoo... Por favor espere
                </Typography>
                <LinearProgress sx={{ width: '100%' }} />
              </Box>
            ) : exportResults ? (
              <Box>
                <Alert 
                  severity={exportResults.errors?.length > 0 ? "warning" : "success"}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Resultado de la Exportación
                  </Typography>
                  <Typography variant="body2">
                    ✅ Exitosas: {exportResults.exported?.length || 0}
                  </Typography>
                  {exportResults.errors?.length > 0 && (
                    <Typography variant="body2" color="error">
                      ❌ Errores: {exportResults.errors.length}
                    </Typography>
                  )}
                </Alert>

                {/* Mostrar detalles de éxitos */}
                {exportResults.exported?.length > 0 && (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
      Facturas Exportadas a Odoo:
    </Typography>
    {exportResults.exported.map((exp, idx) => (
      <Alert key={idx} severity="success" sx={{ mb: 1 }}>
        <Typography variant="body2">
          <strong>MongoDB ID:</strong> {exp.sales_order_id}<br/>
          <strong>✅ Odoo Sale Order:</strong> SO{exp.odoo_order_id}<br/>
          <strong>Líneas procesadas:</strong> {exp.lines_count}<br/>
          <strong>Total facturado:</strong> {BillingUtils.formatCurrency(exp.total_amount)}
        </Typography>
      </Alert>
    ))}
  </Box>
)}

                {/* Mostrar errores */}
                {exportResults.errors?.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" color="error" gutterBottom>
                      Errores Durante la Exportación:
                    </Typography>
                    {exportResults.errors.map((err, idx) => (
                      <Alert key={idx} severity="error" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>SO ID:</strong> {err.sales_order_id}<br/>
                          <strong>Error:</strong> {err.error}
                        </Typography>
                      </Alert>
                    ))}
                  </Box>
                )}

                {/* Botón para cerrar y refrescar */}
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={handleFinishInvoicing}
                  sx={{ mt: 2 }}
                >
                  Finalizar y Refrescar Datos
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={() => {
                  const soIds = createdInvoices.created_sales_orders.map(so => so.sales_order_id);
                  handleExportToOdooFromWizard(soIds);
                }}
                disabled={!createdInvoices.created_sales_orders || createdInvoices.created_sales_orders.length === 0}
                startIcon={<FaFileInvoice />}
              >
                🚀 Generar Factura en Odoo
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Facturas Generadas (JSON para Odoo)
          </Typography>

          {/* Lista de facturas con JSONs */}
          {(!createdInvoices.created_sales_orders || createdInvoices.created_sales_orders.length === 0) ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No se generaron facturas. Verifica los datos de entrada.
            </Alert>
          ) : (
            createdInvoices.created_sales_orders.map((so, index) => (
              <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Factura #{so.sales_order_id || `Temp-${index + 1}`}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Pagador:</strong> {so.payer_info?.name || 'No especificado'} 
                        ({so.payer_info?.type_description || so.payer_type || 'Desconocido'})
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Paciente:</strong> {so.patient_info?.name || 'No especificado'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Total:</strong> {BillingUtils.formatCurrency(so.totals?.net || 0)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Líneas:</strong> {so.lines_count || 0} | 
                        <strong> Actividades:</strong> {so.activities_count || 0}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        JSON para Odoo:
                      </Typography>
                      <Box sx={{ 
                        bgcolor: '#f5f5f5', 
                        p: 2, 
                        borderRadius: 1, 
                        maxHeight: 200, 
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem'
                      }}>
                        <pre>{JSON.stringify(so.odoo_json, null, 2)}</pre>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewJson(so.odoo_json)}
                        >
                          Ver JSON
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleDownloadJson(so.odoo_json)}
                        >
                          Descargar JSON
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={() => handleCopyJson(so.odoo_json)}
                        >
                          Copiar JSON
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))
          )}

          {/* Resumen */}
          {createdInvoices.summary && Object.keys(createdInvoices.summary).length > 0 && (
            <Card variant="outlined" sx={{ mt: 2, bgcolor: '#f5f5f5' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resumen de Procesamiento
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Facturas Creadas:
                    </Typography>
                    <Typography variant="h6">
                      {createdInvoices.created_sales_orders?.length || 
                       createdInvoices.summary.total_sales_orders_created || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Actividades Procesadas:
                    </Typography>
                    <Typography variant="h6">
                      {createdInvoices.summary.total_billable_activities_created || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Total Facturado:
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {BillingUtils.formatCurrency(createdInvoices.summary.total_amount || 0)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );

      default:
        return null;
    }
  };

  const handleActionClick = (action) => {
    if (selectedActivities.length > 0) {
      console.log(`${action} clicked for ${selectedActivities.length} general activities`);
      
      switch(action) {
        case "Print":
          printActivities(selectedActivities);
          break;
        case "Export CSV":
          exportToCSV(selectedActivities);
          break;
        case "Export PDF":
          exportToPDF(selectedActivities);
          break;
        case "Export Excel":
          exportToExcel(selectedActivities);
          break;
        case "Create Billable Activities":
          handleCreateBillableActivities();
          break;
        case "Create Invoices":
          handleOpenInvoiceWizard();
          break;
        default:
          console.warn(`Unknown action: ${action}`);
          break;
      }
    }
  };

  const canCreateBillableActivities = BillingUtils.canCreateBillableActivities && BillingUtils.canCreateBillableActivities(currentUserRoles);
  const canCreateInvoices = true;

const handleLoadCustomerPrice = async (activity, activityKey, pricelistId) => {
  try {
    const customerPrices = customerPricelists.get(activityKey) || [];
    const selectedPricelist = customerPrices.find(p => p._id === pricelistId);
    
    if (!selectedPricelist) {
      alert('❌ Tarifa no encontrada');
      return;
    }

    const price = parseFloat(
      selectedPricelist.price.$numberDecimal || 
      selectedPricelist.price.toString()
    );
    const currency = selectedPricelist.currency || '€';
    
    const payerName = selectedPricelist.companyid?.name || 
                     selectedPricelist.userid?.name || 
                     'Pagador';

    const productName = selectedPricelist.productid?.name || 
                       selectedPricelist.productid?.productName || 
                       'Producto sin nombre';

    // 🆕 Guardar la selección del dropdown
    const newSelected = new Map(selectedCustomerPricelist);
    newSelected.set(activityKey, pricelistId);
    setSelectedCustomerPricelist(newSelected);

    // Actualizar el precio mostrado en la caja negra
    const newDisplayedPrices = new Map(displayedCustomerPrices);
    newDisplayedPrices.set(activityKey, { price, currency });
    setDisplayedCustomerPrices(newDisplayedPrices);

    // Guardar información del precio cargado
    const newLoadedPrices = new Map(loadedPrices);
    newLoadedPrices.set(activityKey, {
      type: 'customer',
      price: price,
      currency: currency,
      pricelist_id: selectedPricelist._id,
      payer_id: selectedPricelist.companyid?._id || selectedPricelist.userid?._id,
      payer_type: selectedPricelist.companyid ? 'Company' : 'User',
      payer_name: payerName,
      product_id: selectedPricelist.productid?._id,
      product_name: productName,
      details: selectedPricelist
    });
    setLoadedPrices(newLoadedPrices);

    console.log('✅ Precio de cliente cargado:', { price, currency, payerName, productName });

  } catch (error) {
    console.error('❌ Error cargando precio de cliente:', error);
    alert(`Error: ${error.message}`);
  }
};

const handleLoadSupplierPrice = async (activity, activityKey, pricelistId) => {
  try {
    const supplierPrices = supplierPricelists.get(activityKey) || [];
    const selectedPricelist = supplierPrices.find(p => p._id === pricelistId);
    
    if (!selectedPricelist) {
      alert('❌ Tarifa no encontrada');
      return;
    }

    const price = parseFloat(
      selectedPricelist.price.$numberDecimal || 
      selectedPricelist.price.toString()
    );
    const currency = selectedPricelist.currency || '€';
    
    const supplierName = selectedPricelist.supplierid?.name || 
                        selectedPricelist.userid?.name || 
                        'Proveedor';

    const productName = selectedPricelist.productid?.name || 
                       selectedPricelist.productid?.productName || 
                       'Producto sin nombre';

    // 🆕 Guardar la selección del dropdown
    const newSelected = new Map(selectedSupplierPricelist);
    newSelected.set(activityKey, pricelistId);
    setSelectedSupplierPricelist(newSelected);

    // Actualizar el precio mostrado en la caja negra
    const newDisplayedPrices = new Map(displayedSupplierPrices);
    newDisplayedPrices.set(activityKey, { price, currency });
    setDisplayedSupplierPrices(newDisplayedPrices);

    // Guardar información del precio cargado
    const newLoadedPrices = new Map(loadedPrices);
    newLoadedPrices.set(activityKey, {
      type: 'supplier',
      price: price,
      currency: currency,
      pricelist_id: selectedPricelist._id,
      payer_id: selectedPricelist.supplierid?._id || selectedPricelist.userid?._id,
      payer_type: selectedPricelist.supplierid ? 'Company' : 'User',
      payer_name: supplierName,
      product_id: selectedPricelist.productid?._id,
      product_name: productName,
      details: selectedPricelist
    });
    setLoadedPrices(newLoadedPrices);

    console.log('✅ Precio de proveedor cargado:', { price, currency, supplierName, productName });

  } catch (error) {
    console.error('❌ Error cargando precio de proveedor:', error);
    alert(`Error: ${error.message}`);
  }
};

const handleLoadProductPrice = async (activity, activityKey, productId) => {
  try {
    const products = productPricelists.get(activityKey) || [];
    const selectedProduct = products.find(p => p._id === productId);
    
    if (!selectedProduct) {
      alert('❌ Producto no encontrado');
      return;
    }

    // Extraer precio del producto
    const price = parseFloat(
      selectedProduct.priceExcludingVAT?.$numberDecimal || 
      selectedProduct.priceExcludingVAT || 
      selectedProduct.price?.$numberDecimal ||
      selectedProduct.price ||
      0
    );
    
    const currency = '€';
    const productName = selectedProduct.productName || selectedProduct.name || 'Producto';

    // Guardar la selección del dropdown
    const newSelected = new Map(selectedProductPricelist);
    newSelected.set(activityKey, productId);
    setSelectedProductPricelist(newSelected);

    // Actualizar el precio mostrado en la caja negra
    const newDisplayedPrices = new Map(displayedProductPrices);
    newDisplayedPrices.set(activityKey, { price, currency });
    setDisplayedProductPrices(newDisplayedPrices);

    // Guardar información del precio cargado
    const newLoadedPrices = new Map(loadedPrices);
    newLoadedPrices.set(activityKey, {
      type: 'product',
      price: price,
      currency: currency,
      product_id: selectedProduct._id,
      product_name: productName,
      details: selectedProduct
    });
    setLoadedPrices(newLoadedPrices);

    console.log('✅ Precio de producto cargado:', { price, currency, productName });

  } catch (error) {
    console.error('❌ Error cargando precio de producto:', error);
    alert(`Error: ${error.message}`);
  }
};


  return (
    <>
      <div className="flex-grow-sm-0" style={{ position: 'relative', zIndex: 1000 }}>
        <Dropdown align="end">
          <Dropdown.Toggle
            id="general-action-dropdown"
            className={`btn btn-gray text-start w-100 d-flex align-items-center justify-content-between px-4 py-2 border-0 rounded-3 shadow-sm dropdownbtn`}
            disabled={isCreating || isProcessingBulkAction}
            style={{ minWidth: '120px' }}
          >
            <span>
              {isCreating ? 'Processing...' : 
               isProcessingBulkAction ? 'Processing...' :
               `Action${selectedActivities.length > 0 ? ` (${selectedActivities.length})` : ''}`}
            </span>
          </Dropdown.Toggle>

          <Dropdown.Menu 
            className="bg-white fs-sm fw-semibold border rounded-3 shadow-lg"
            style={{ 
              minWidth: '280px',
              zIndex: 1050
            }}
          >
            <Dropdown.Item
              as="button"
              onClick={() => handleActionClick("Print")}
              className="d-flex align-items-center gap-2 py-2 px-3"
              disabled={selectedActivities.length === 0 || isCreating || isProcessingBulkAction}
            >
              <FaDownload className="icon-sm" />
              <span>Print Activities</span>
            </Dropdown.Item>

            <Dropdown.Divider className="my-1" />

            <Dropdown.Item
              as="button"
              onClick={() => handleActionClick("Export CSV")}
              className="d-flex align-items-center gap-2 py-2 px-3"
              disabled={selectedActivities.length === 0 || isCreating || isProcessingBulkAction}
            >
              <FaFileCsv className="icon-sm" />
              <span>Download CSV</span>
            </Dropdown.Item>
            
            <Dropdown.Item
              as="button"
              onClick={() => handleActionClick("Export PDF")}
              className="d-flex align-items-center gap-2 py-2 px-3"
              disabled={selectedActivities.length === 0 || isCreating || isProcessingBulkAction}
            >
              <FaFilePdf className="icon-sm" />
              <span>Download PDF</span>
            </Dropdown.Item>
            
            <Dropdown.Item
              as="button"
              onClick={() => handleActionClick("Export Excel")}
              className="d-flex align-items-center gap-2 py-2 px-3"
              disabled={selectedActivities.length === 0 || isCreating || isProcessingBulkAction}
            >
              <FaFileExcel className="icon-sm" />
              <span>Download Excel</span>
            </Dropdown.Item>

            {(canCreateBillableActivities || canCreateInvoices) && <Dropdown.Divider className="my-1" />}

            {canCreateBillableActivities && (
              <Dropdown.Item
                as="button"
                onClick={() => handleActionClick("Create Billable Activities")}
                className="d-flex align-items-center gap-2 py-2 px-3"
                disabled={selectedActivities.length === 0 || isCreating || isProcessingBulkAction}
              >
                <FaPlus className="icon-sm" />
                <span>{isCreating ? 'Creating...' : 'Create Billable Activities'}</span>
              </Dropdown.Item>
            )}

            {canCreateInvoices && (
              <Dropdown.Item
                as="button"
                onClick={() => handleActionClick("Create Invoices")}
                className="d-flex align-items-center gap-2 py-2 px-3"
                disabled={selectedActivities.length === 0 || isCreating || isProcessingBulkAction}
              >
                <FaFileInvoice className="icon-sm" />
                <span>Create Invoices (Odoo JSON)</span>
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Offcanvas para Crear Facturas */}
      <Offcanvas
        show={showInvoiceOffcanvas}
        onHide={handleCloseInvoiceOffcanvas}
        placement="end"
        backdrop="static"
        keyboard={false}
        ref={offcanvasRef}
        style={{ width: '90%', maxWidth: '1400px' }}
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>
            <div className="d-flex align-items-center gap-2">
              <FaFileInvoice />
              <span>Crear Facturas para Odoo</span>
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="p-0">
          {/* Progress Bar */}
          <div className="px-4 pt-4 pb-2">
            <LinearProgress 
              variant="determinate" 
              value={(activeStep / (wizardSteps.length - 1)) * 100}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <div className="d-flex justify-content-between mt-2">
              {wizardSteps.map((step, index) => (
                <div 
                  key={index}
                  className={`text-center flex-fill ${index === activeStep ? 'text-primary fw-bold' : 'text-muted'}`}
                  style={{ fontSize: '0.875rem' }}
                >
                  <div className={`badge ${index === activeStep ? 'bg-primary' : index < activeStep ? 'bg-success' : 'bg-secondary'} mb-1`}>
                    {index + 1}
                  </div>
                  <div>{step}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div style={{ height: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            {renderOffcanvasStep()}
          </div>

          {/* Navigation Footer */}
          <div className="border-top p-3 bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <Button
                variant="outlined"
                onClick={handleBackStep}
                disabled={activeStep === 0 || isCreating}
              >
                Anterior
              </Button>

              <Typography variant="body2" color="textSecondary">
                Paso {activeStep + 1} de {wizardSteps.length}
              </Typography>

              {activeStep < wizardSteps.length - 1 ? (
                activeStep === 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateInvoices}
                    disabled={isCreating}
                    startIcon={isCreating ? <CircularProgress size={20} /> : <FaFileInvoice />}
                  >
                    {isCreating ? 'Creando...' : 'Crear Facturas'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNextStep}
                    disabled={!areAllSplitsValid()}
                  >
                    Siguiente
                  </Button>
                )
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleFinishInvoicing}
                >
                  Finalizar
                </Button>
              )}
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default BillingActionButtonGeneral;